import { HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthService } from '@libs/shared/auth';
import {
  NewShopItem,
  ProductListPayloadModel,
  ShopCollectionResponseModel,
  ShopErrorCollectionModel,
  ShoppingListItemModel,
  ShoppingListModel,
} from '@libs/shop/models';
import { ConnectionParams, FileModel } from '@libs/shared/models';
import { filter, map, Observable, switchMap } from 'rxjs';
import { ConnectorService } from '../connector';
import { FileSaverService } from '../file-saver';

@Injectable({ providedIn: 'root' })
export class ShoppingListsRepositoryService {
  public constructor(
    private readonly connector: ConnectorService,
    private readonly authService: AuthService,
    private readonly fileSaver: FileSaverService,
  ) {
  }

  public initShoppingLists(): Observable<ShoppingListModel[]> {
    return this.authService.getCurrentCustomer().pipe(filter(x => !!x), switchMap(() => {
      return this.connector.connect<ShoppingListModel[]>('shop', 'shoppingLists');
    }));
  }

  public getShoppingListById(listId: string): Observable<ShoppingListModel> {
    return this.connector.connect<ShoppingListModel>('shop', 'shoppingList', {
      pathParams: {
        listId,
      },
    });
  }

  public createShoppingList(name: string): Observable<ShoppingListModel> {
    return this.connector.connect<ShoppingListModel>('shop', 'shoppingListsCreate', {
      payload: {
        type: 'shopping-list',
        name,
      },
    });
  }

  public updateShoppingList(list: ShoppingListModel): Observable<ShoppingListModel> {
    return this.connector.connect<ShoppingListModel>('shop', 'shoppingListsUpdate', {
      payload: { ...list, items: [] } satisfies ShoppingListModel,
      pathParams: {
        listId: list.id,
      },
    });
  }

  public deleteShoppingList(listId: string): Observable<void> {
    return this.connector.connect('shop', 'shoppingListsDelete', {
      pathParams: {
        listId,
      },
    });
  }

  public addProductToShoppingList(listId: string, sku: string, quantity: number): Observable<ShoppingListItemModel> {
    const payload: NewShopItem<ShoppingListItemModel> = {
      type: 'shopping-list-items',
      sku,
      quantity,
    };

    const params: ConnectionParams = {
      payload,
      pathParams: { listId },
    };

    return this.connector.connect<ShoppingListItemModel>('shop', 'shoppingListsAddProduct', params);
  }

  public addProductCollectionToShoppingList(listId: string, collection: ProductListPayloadModel[]): Observable<ShopCollectionResponseModel<ShoppingListModel>> {
    return this.connector.connect<ShopCollectionResponseModel<ShoppingListModel>>('shop', 'shoppingListsAddProductCollection', {
      pathParams: {
        listId: listId,
      },
      payload: collection,
    });
  }

  public updateProductInShoppingList(listId: string, item: ShoppingListItemModel): Observable<ShoppingListItemModel> {
    return this.connector.connect<ShoppingListItemModel>('shop', 'shoppingListsUpdateProduct', {
      payload: item,
      pathParams: { listId, itemId: item.id },
    });
  }

  public deleteProductFromShoppingList(listId: string, item: ShoppingListItemModel): Observable<void> {
    return this.connector.connect<void>('shop', 'shoppingListsDeleteProduct', {
      pathParams: { listId, itemId: item.id },
    });
  }

  public deleteProductsFromShoppingListCollection(listId: string, collection: ShoppingListItemModel[]): Observable<ShopErrorCollectionModel> {
    const mappedCollection: ProductListPayloadModel[] = collection.map(x => {
      return {
        type: x.type,
        id: x.id,
        sku: x.sku,
        quantity: x.quantity,
      };
    });

    return this.connector.connect<ShopErrorCollectionModel>('shop', 'shoppingListsDeleteProductsCollection', {
      pathParams: { listId },
      payload: mappedCollection,
    });
  }

  public downloadShoppingList(listId: string): Observable<FileModel> {
    return this.connector.connect<HttpResponse<Blob>>(
      'shop',
      'shoppingListDownload',
      {
        pathParams: { listId },
        responseType: 'blob',
        observe: 'response',
      }).pipe(map((response) => this.fileSaver.mapHttpResponseBlobToFileModel(response)));
  }
}
